/**
 * The error every failed API call rejects with.
 *
 * `ErrorInterceptor` used to rethrow `new Error(message)`, which threw away the HTTP status and
 * the server's error envelope. Callers written against `err.status` therefore compared
 * `undefined`, and the comparison silently took the wrong branch — the MeetHour join gate is the
 * worst case found: `if (err?.status !== 403)` was meant to refuse a suspended or unpaid learner
 * and instead built the room URL for every refusal, so the gate was open on the client for as
 * long as the interceptor has existed.
 *
 * `message` is unchanged, so the many callers reading `err.message` keep working; the fields
 * below are additive.
 */
export class ApiError extends Error {
  /** HTTP status. `0` for a network-level failure, matching `HttpErrorResponse`. */
  readonly status: number;
  /** The server envelope's `name` (`NotAuthenticated`, `Forbidden`, …), when it sent one. */
  readonly serverName: string | null;
  /** The envelope's `data`, which carries `reason` for account-state rejections. */
  readonly data: unknown;
  /** The untouched error body, for anything that needs more than the fields above. */
  readonly body: unknown;

  constructor(
    message: string,
    init: { status: number; serverName?: unknown; data?: unknown; body?: unknown }
  ) {
    super(message);
    // Left as "Error" on purpose: `name` is what existing code compares when it compares
    // anything, and renaming it here would change behaviour that has nothing to do with status.
    this.name = "Error";
    this.status = init.status;
    this.serverName = typeof init.serverName === "string" ? init.serverName : null;
    this.data = init.data ?? null;
    this.body = init.body ?? null;
  }
}
